import {
    ChangeEvent,
    FC,
    KeyboardEvent,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import classnames from 'classnames'
import type { LinkPreview, LocalAttachment, LocalMessage, Suggestions, UserSuggestion } from 'stream-chat'
import { LinkPreviewsManager, MessageComposer } from 'stream-chat'
import {
    useChannelActionContext,
    useChannelStateContext,
    useChatContext,
    useMessageComposer,
    useMessageInputContext,
    useStateStore,
} from 'stream-chat-react'

import { Avatar, Icon, Textarea } from '@/components'
import { IconButton, ImageViewer } from '@/compositions'
import { useEditingMessage } from '@/context/EditingMessageContext'
import { convertEmoticonBeforeCursor, EmoticonConversion, revertEmoticonConversion } from '@/helpers/emoticons'
import { getInitials } from '@/helpers/name'
import { trans } from '@/helpers/trans'

import './ConversationMessageInput.scss'

const textComposerStateSelector = (state: {
    selection: { start: number, end: number }
    suggestions?: Suggestions
    text: string
}) => ({
    selection: state.selection,
    suggestions: state.suggestions,
    text: state.text,
})

const suggestionItemsStateSelector = (state: { items?: unknown[] }) => ({
    items: state.items ?? [],
})

const configStateSelector = (state: { text: { enabled: boolean } }) => ({
    enabled: state.text.enabled,
})

const attachmentsStateSelector = (state: { attachments: LocalAttachment[] }) => ({
    attachments: state.attachments,
})

const linkPreviewsStateSelector = (state: { previews: Map<string, LinkPreview> }) => ({
    linkPreviews: Array.from(state.previews.values()).filter(preview => (
        LinkPreviewsManager.previewIsLoaded(preview) || LinkPreviewsManager.previewIsLoading(preview)
    )),
})

/**
 * Works around a bug in stream-chat's own link-previews composition middleware
 */
const withSkipEnrichUrlFix = <T extends { skip_enrich_url?: boolean } | undefined>(
    linkPreviewsManager: LinkPreviewsManager,
    sendOptions: T,
): T => {
    const someLinkPreviewsLoading = linkPreviewsManager.loadingPreviews.length > 0
    const someLinkPreviewsDismissed = linkPreviewsManager.dismissedPreviews.length > 0
    const skipEnrichUrl = (! someLinkPreviewsLoading && linkPreviewsManager.loadedPreviews.length > 0)
        || someLinkPreviewsDismissed

    return skipEnrichUrl ? { ...sendOptions, skip_enrich_url: true } : sendOptions
}

const findLastOwnMessage = (
    messages: LocalMessage[] | undefined,
    userId: string | undefined,
): LocalMessage | undefined => {
    for (let index = (messages?.length ?? 0) - 1; index >= 0; index -= 1) {
        const candidate = messages?.[index]

        if (
            candidate
            && candidate.user?.id === userId
            && ! candidate.deleted_at
            && (! candidate.type || candidate.type === 'regular')
        ) {
            return candidate
        }
    }

    return undefined
}

/**
 * Stream's own textarea (react-textarea-autosize) doesn't prevent the newline it inserts on
 * Enter before our submit handler clears the field, which is visible as a one-frame flash of a
 * taller, scrollable textarea. Using our own autoGrow Textarea - the same one used for feed post
 * comments - avoids that, and keeps the chat and feed input styling consistent.
 */
export const ConversationMessageInput: FC = (): ReactElement => {
    const { cooldownRemaining, textareaRef } = useMessageInputContext()
    const { client } = useChatContext()
    const { addNotification, editMessage, sendMessage } = useChannelActionContext()
    const { channelCapabilities, messages } = useChannelStateContext()
    const { editingMessage, setEditingMessage } = useEditingMessage()
    const channelComposer = useMessageComposer()

    const editComposer = useMemo(() => {
        if (! editingMessage) {
            return undefined
        }

        return new MessageComposer({
            client,
            composition: editingMessage,
            compositionContext: editingMessage,
            config: { linkPreviews: { enabled: true } },
        })
    }, [client, editingMessage])

    useEffect(() => editComposer?.registerSubscriptions(), [editComposer])

    const isEditing = !! editComposer
    const activeComposer = editComposer ?? channelComposer
    const { attachmentManager, linkPreviewsManager, textComposer } = activeComposer
    const { selection, suggestions, text } = useStateStore(textComposer.state, textComposerStateSelector)
    const { enabled } = useStateStore(activeComposer.configState, configStateSelector)
    const { attachments } = useStateStore(attachmentManager.state, attachmentsStateSelector)
    const { linkPreviews } = useStateStore(linkPreviewsManager.state, linkPreviewsStateSelector)
    const { items: suggestionItems } = useStateStore(suggestions?.searchSource.state, suggestionItemsStateSelector) ?? {}

    const mentionSuggestions = (suggestions?.trigger === '@' ? suggestionItems : undefined)
        ?.filter((item): item is UserSuggestion => (
            !! item && typeof item === 'object' && 'mentionType' in item && item.mentionType === 'user'
        )) ?? []
    const [focusedMentionIndex, setFocusedMentionIndex] = useState(0)

    const [previousSuggestions, setPreviousSuggestions] = useState(suggestions)

    if (suggestions !== previousSuggestions) {
        setPreviousSuggestions(suggestions)
        setFocusedMentionIndex(0)
    }

    const isEmpty = ! text.trim() && ! attachments.length

    const lastOwnMessage = channelCapabilities['update-own-message']
        ? findLastOwnMessage(messages, client.userID)
        : undefined
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [viewerIndex, setViewerIndex] = useState<number | null>(null)
    const [isSavingEdit, setIsSavingEdit] = useState(false)
    const isDisabled = ! enabled || !! cooldownRemaining || (isEditing && isSavingEdit)
    const [objectUrlsById, setObjectUrlsById] = useState<Map<string, string>>(new Map())

    useEffect(() => {
        const urls = new Map<string, string>()

        attachments.forEach(attachment => {
            const { file } = attachment.localMetadata

            if (file instanceof File) {
                urls.set(attachment.localMetadata.id, URL.createObjectURL(file))
            }
        })

        // Value can't be adjusted during render: it only exists after URL.createObjectURL() actually runs
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setObjectUrlsById(urls)

        return () => {
            urls.forEach(url => URL.revokeObjectURL(url))
        }
    }, [attachments])

    const getAttachmentImageUrl = (attachment: LocalAttachment): string | undefined => (
        objectUrlsById.get(attachment.localMetadata.id)
            ?? (attachment.type === 'image' ? attachment.image_url : undefined)
    )

    useEffect(() => {
        activeComposer.updateConfig({
            attachments: { acceptedFiles: ['image/*'] },
            linkPreviews: { enabled: true },
        })
    }, [activeComposer])

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.selectionStart = selection.start
            textareaRef.current.selectionEnd = selection.end
        }
    }, [text, selection.start, selection.end, textareaRef])

    const lastEmoticonConversionRef = useRef<EmoticonConversion | null>(null)

    const setTextWithCursor = useCallback((nextText: string, cursor: number): void => {
        void textComposer.handleChange({
            selection: { end: cursor, start: cursor },
            text: nextText,
        })
    }, [textComposer])

    /**
     * Undoes the emoticon conversion that was just made, but only while the cursor is still right after its emoji
     */
    const revertLastEmoticonConversion = useCallback((): boolean => {
        const conversion = lastEmoticonConversionRef.current

        lastEmoticonConversionRef.current = null

        if (! conversion || selection.end !== conversion.start + conversion.emoji.length) {
            return false
        }

        const reverted = revertEmoticonConversion(text, conversion)

        if (reverted) {
            setTextWithCursor(reverted.text, reverted.cursor)
        }

        return !! reverted
    }, [selection.end, text, setTextWithCursor])

    const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>): void => {
        const { selectionEnd, selectionStart, value } = event.target
        const inputType = event.nativeEvent instanceof InputEvent ? event.nativeEvent.inputType : undefined

        // Backspace right after a conversion restores the emoticon, as there's no Ctrl+Z on mobile
        if (inputType === 'deleteContentBackward' && revertLastEmoticonConversion()) {
            return
        }

        lastEmoticonConversionRef.current = null

        // Only for typed characters, so pasted text and undo/redo are left as-is
        const converted = inputType === 'insertText' && selectionStart === selectionEnd
            ? convertEmoticonBeforeCursor(value, selectionEnd)
            : undefined

        if (converted) {
            lastEmoticonConversionRef.current = converted.conversion
            setTextWithCursor(converted.text, converted.cursor)

            return
        }

        void textComposer.handleChange({
            selection: {
                end: selectionEnd,
                start: selectionStart,
            },
            text: value,
        })
    }, [textComposer, revertLastEmoticonConversion, setTextWithCursor])

    const handleFilesSelected = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
        if (event.target.files?.length) {
            void attachmentManager.uploadFiles(event.target.files)
        }

        event.target.value = ''
    }, [attachmentManager])

    const removeAttachment = useCallback((id: string): void => {
        attachmentManager.removeAttachments([id])
    }, [attachmentManager])

    const removeLinkPreviewImage = useCallback((url: string): void => {
        linkPreviewsManager.updatePreview(url, { image_url: '', thumb_url: '' })
    }, [linkPreviewsManager])

    const dismissLinkPreview = useCallback((url: string): void => {
        linkPreviewsManager.dismissPreview(url)
    }, [linkPreviewsManager])

    const selectMention = useCallback((suggestion: UserSuggestion): void => {
        void textComposer.handleSelect(suggestion)
    }, [textComposer])

    const handleTextareaKeyDown = useCallback((event: KeyboardEvent<HTMLTextAreaElement>): void => {
        const isUndo = event.key.toLowerCase() === 'z' && (event.ctrlKey || event.metaKey) && ! event.shiftKey

        if (isUndo && revertLastEmoticonConversion()) {
            event.preventDefault()

            return
        }

        if (mentionSuggestions.length) {
            if (event.key === 'Escape') {
                textComposer.closeSuggestions()
            } else if (event.key === 'Enter') {
                // Prevents the plain Textarea's own Enter handling from also submitting right
                // after - see Textarea.tsx's defaultPrevented check.
                event.preventDefault()
                selectMention(mentionSuggestions[focusedMentionIndex])
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                setFocusedMentionIndex(index => (index <= 0 ? mentionSuggestions.length - 1 : index - 1))
            } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                setFocusedMentionIndex(index => (index >= mentionSuggestions.length - 1 ? 0 : index + 1))
            }

            return
        }

        // Enable edit on last own message when pressing up key when the message input is empty and focused
        if (event.key === 'ArrowUp' && ! isDisabled && isEmpty) {
            if (editingMessage) {
                event.preventDefault()
                setEditingMessage(null)
            } else if (lastOwnMessage) {
                event.preventDefault()
                setEditingMessage(lastOwnMessage)
            }
        }
    }, [
        revertLastEmoticonConversion,
        mentionSuggestions,
        focusedMentionIndex,
        textComposer,
        selectMention,
        isDisabled,
        isEmpty,
        editingMessage,
        lastOwnMessage,
        setEditingMessage,
    ])

    const cancelEdit = useCallback((): void => {
        setEditingMessage(null)
    }, [setEditingMessage])

    const saveEdit = useCallback(async (): Promise<void> => {
        if (! editComposer || isSavingEdit) {
            return
        }

        setIsSavingEdit(true)

        try {
            const composition = await editComposer.compose()

            if (composition?.localMessage) {
                const sendOptions = withSkipEnrichUrlFix(editComposer.linkPreviewsManager, composition.sendOptions)

                await editMessage(composition.localMessage, sendOptions)
                setEditingMessage(null)
            }
        } finally {
            setIsSavingEdit(false)
        }
    }, [editComposer, isSavingEdit, editMessage, setEditingMessage])

    const submitNewMessage = useCallback(async (): Promise<void> => {
        const composition = await channelComposer.compose()

        if (! composition?.message) {
            return
        }

        const { localMessage, message, sendOptions } = composition
        const finalSendOptions = withSkipEnrichUrlFix(channelComposer.linkPreviewsManager, sendOptions)
        const restoreComposerState = {
            attachmentManager: channelComposer.attachmentManager.state.getLatestValue(),
            linkPreviewsManager: channelComposer.linkPreviewsManager.state.getLatestValue(),
            textComposer: channelComposer.textComposer.state.getLatestValue(),
        }

        channelComposer.clear()

        try {
            await sendMessage({ localMessage, message, options: finalSendOptions })
        } catch {
            channelComposer.attachmentManager.state.next(restoreComposerState.attachmentManager)
            channelComposer.linkPreviewsManager.state.next(restoreComposerState.linkPreviewsManager)
            channelComposer.textComposer.state.next(restoreComposerState.textComposer)
            addNotification(trans('pages.messages.sendMessageFailed'), 'error')
        }
    }, [channelComposer, sendMessage, addNotification])

    const submit = isEditing ? saveEdit : submitNewMessage
    const previewImages = attachments.map(attachment => getAttachmentImageUrl(attachment) ?? '')

    return (
        <div className={classnames('conversation-message-input', { 'conversation-message-input--editing': isEditing })}>
            {!! attachments.length && (
                <ul className="conversation-message-input__attachments">
                    {attachments.map((attachment, index) => (
                        <li
                            key={attachment.localMetadata.id}
                            data-upload-state={attachment.localMetadata.uploadState}
                            className="conversation-message-input__attachment"
                        >
                            <button
                                type="button"
                                onClick={() => setViewerIndex(index)}
                                aria-label={trans('common.view')}
                                className="conversation-message-input__attachment-preview"
                            >
                                <img
                                    src={getAttachmentImageUrl(attachment)}
                                    alt=""
                                    className="conversation-message-input__attachment-image"
                                />
                            </button>
                            <button
                                type="button"
                                onClick={() => removeAttachment(attachment.localMetadata.id)}
                                aria-label={trans('common.delete')}
                                className="conversation-message-input__attachment-remove"
                            >
                                <Icon name="cross-close" className="conversation-message-input__attachment-remove-icon" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {!! linkPreviews.length && (
                <ul className="conversation-message-input__link-previews">
                    {linkPreviews.map((linkPreview) => {
                        const previewImage = linkPreview.thumb_url || linkPreview.image_url

                        return (
                            <li
                                key={linkPreview.og_scrape_url}
                                data-loading={LinkPreviewsManager.previewIsLoading(linkPreview) || undefined}
                                className="conversation-message-input__link-preview"
                            >
                                {!! previewImage && (
                                    <div className="conversation-message-input__link-preview-image-wrapper">
                                        <img
                                            src={previewImage}
                                            alt=""
                                            className="conversation-message-input__link-preview-image"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeLinkPreviewImage(linkPreview.og_scrape_url)}
                                            aria-label={trans('pages.messages.removeLinkPreviewImage')}
                                            className="conversation-message-input__link-preview-image-remove"
                                        >
                                            <Icon name="cross-close" className="conversation-message-input__link-preview-remove-icon" />
                                        </button>
                                    </div>
                                )}
                                <div className="conversation-message-input__link-preview-content">
                                    <strong className="conversation-message-input__link-preview-title">
                                        {linkPreview.title || linkPreview.og_scrape_url}
                                    </strong>
                                    {!! linkPreview.text && (
                                        <span className="conversation-message-input__link-preview-text">
                                            {linkPreview.text}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => dismissLinkPreview(linkPreview.og_scrape_url)}
                                    aria-label={trans('pages.messages.removeLinkPreview')}
                                    className="conversation-message-input__link-preview-remove"
                                >
                                    <Icon name="cross-close" className="conversation-message-input__link-preview-remove-icon" />
                                </button>
                            </li>
                        )
                    })}
                </ul>
            )}

            <div className="conversation-message-input__row">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={handleFilesSelected}
                />
                <IconButton
                    hideLabel
                    icon="plus"
                    text={trans('pages.messages.addImage')}
                    disabled={isDisabled || ! attachmentManager.isUploadEnabled}
                    onClick={() => fileInputRef.current?.click()}
                    className="conversation-message-input__attach"
                />
                <Textarea
                    ref={(ref) => {
                        textareaRef.current = ref
                    }}
                    autoGrow
                    minRows={1}
                    maxRows={6}
                    spellCheck
                    autoCorrect="on"
                    autoCapitalize="sentences"
                    value={text}
                    disabled={isDisabled}
                    name="message"
                    title={trans('pages.messages.messagePlaceholder')}
                    placeholder={trans('pages.messages.messagePlaceholder')}
                    enterKeyHint={isEditing ? 'done' : 'send'}
                    className="conversation-message-input__textarea"
                    onChange={handleChange}
                    onKeyDown={handleTextareaKeyDown}
                    onSubmit={submit}
                />
                {!! mentionSuggestions.length && (
                    <ul
                        role="listbox"
                        aria-label={trans('pages.messages.mentionSuggestions')}
                        className="conversation-message-input__mentions"
                    >
                        {mentionSuggestions.map((suggestion, index) => (
                            <li key={suggestion.id}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={index === focusedMentionIndex}
                                    onMouseEnter={() => setFocusedMentionIndex(index)}
                                    onClick={() => selectMention(suggestion)}
                                    className={classnames('conversation-message-input__mention', {
                                        'conversation-message-input__mention--focused': index === focusedMentionIndex,
                                    })}
                                >
                                    <Avatar
                                        className="conversation-message-input__mention-avatar"
                                        image={suggestion.image}
                                        initials={getInitials(suggestion.name)}
                                        name={suggestion.name}
                                    />
                                    <span className="conversation-message-input__mention-name">
                                        {suggestion.name || suggestion.id}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                {isEditing && (
                    <IconButton
                        hideLabel
                        icon="cross-close"
                        text={trans('common.cancel')}
                        disabled={isSavingEdit}
                        onClick={cancelEdit}
                        className="conversation-message-input__cancel"
                    />
                )}
                <IconButton
                    hideLabel
                    icon={isEditing ? 'check' : 'arrow'}
                    text={trans(isEditing ? 'common.save' : 'common.send')}
                    disabled={isDisabled || isEmpty}
                    onClick={submit}
                    className="conversation-message-input__submit"
                    iconClassName={isEditing ? undefined : 'conversation-message-input__submit-icon'}
                />
            </div>

            {viewerIndex !== null && (
                <ImageViewer
                    images={previewImages}
                    initialIndex={viewerIndex}
                    onClose={() => setViewerIndex(null)}
                />
            )}
        </div>
    )
}
